// @flow
import React, { useContext } from 'react';
import { PaddedAndRaisedSegment } from '@presentational/PaddedAndRaisedSegment';
import { GenericSaveResetButtons } from '@components/GenericSaveResetButtons';
import { Spacer } from '@presentational/spacing/Spacer';
import { Grid } from 'semantic-ui-react';
import { useUpdateClusterWorkloadMutation } from '@streaming-projects/service-definitions/streamingProjectsApi';
import { useParams } from 'react-router-dom';
import { SPStreamingProjectContext } from '@streaming-projects/sp-page/contexts/SPStreamingProjectContext';
import { RetentionApprovalRequiredContainer } from '@src/triggers/RetentionApprovalRequiredContainer';
import {
  isAnythingEditedBesidesGivenFormBasedOnGivenConfig,
  shouldShowFFOption,
} from '@streaming-projects/utils';
import useIsAdmin from '@streaming-projects/useIsAdmin';
import { useFormikContext } from 'formik';
import { FORM_NAMES } from '@streaming-projects/enums';
import { If } from '@presentational/If';
import { IS_TABLE_FLOW_LIVE } from '@streaming-projects/constants';
import { SPClusterWorkloadNetworkingTypeContainer } from '@streaming-projects/workloads/cluster-workloads/workload-details/SPClusterWorkloadNetworkingTypeContainer';

import { useSPClusterWorkloadContext } from '../contexts/SPClusterWorkloadContextProvider';
import { CLUSTER_WORKLOAD_DETAILS_CONFIG, CLUSTER_WORKLOAD_DETAILS_INPUT_CONFIG } from '../config';

import { SPClusterWorkloadNameContainer } from './SPClusterWorkloadNameContainer';
import { SPRetentionContainer } from './SPRetentionContainer';
import { SPRetentionInfiniteContainer } from './SPRetentionInfiniteContainer';
import { SPClusterFollowerFetchContainer } from './SPClusterFollowerFetchContainer';
import { SPClusterWorkloadExistingStorageContainer } from './SPClusterWorkloadExistingStorageContainer';
import { SPClusterWorkloadCKUContainer } from './SPClusterWorkloadCKUContainer';
import { SPClusterTableFlowEnabledContainer } from './SPClusterTableFlowEnabledContainer';

export const SPClusterWorkloadDetailsContainer = () => {
  const { values, initialValues } = useFormikContext();
  const { orgId, spId, clusterWorkloadId } = useParams();
  const [updateClusterWorkload] = useUpdateClusterWorkloadMutation();
  const spStreamingProjectData = useContext(SPStreamingProjectContext);

  const spClusterWorkloadData = useSPClusterWorkloadContext();

  const isUserAdmin = useIsAdmin();

  const shouldShowFFOptionVariable = shouldShowFFOption(
    spClusterWorkloadData.cluster_resource_details,
    isUserAdmin
  );

  const { isBaseLineSP } = spStreamingProjectData;

  const shouldFormBeDisabled = isAnythingEditedBesidesGivenFormBasedOnGivenConfig(
    initialValues,
    values,
    CLUSTER_WORKLOAD_DETAILS_CONFIG,
    FORM_NAMES.CLUSTER_WORKLOAD.WORKLOAD_DETAILS_FORM
  );
  return (
    <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
      <GenericSaveResetButtons
        fieldsConfig={CLUSTER_WORKLOAD_DETAILS_INPUT_CONFIG}
        onSaveHandler={async ({
          name,
          retention_days: retentionDays,
          retention_infinite: retentionInfinite,
          is_follower_fetch_enabled: isFollowerFetchEnabled = false,
          is_tableflow_enabled: isTableflowEnabled = false,
          networking_type: clusterWorkloadNetworkingType = false,
          existing_storage: existingStorage,
          cku,
        }) => {
          return await updateClusterWorkload({
            orgId,
            spId,
            clusterWorkloadId,
            payload: {
              cluster_resource_id: spClusterWorkloadData.cluster_resource_id,
              sp_version: spStreamingProjectData?.sp?.version,
              name,
              inputs: {
                is_follower_fetch_enabled: isFollowerFetchEnabled,
                is_tableflow_enabled: isTableflowEnabled,
                networking_type: clusterWorkloadNetworkingType,
                retention: {
                  retention_days: retentionDays,
                  is_finite_retention: !retentionInfinite,
                },
              },
              metadata: {
                existing_storage: existingStorage,
                cku,
              },
            },
          });
        }}
      />
      <Spacer y={20} />
      <Grid>
        <Grid.Row>
          <Grid.Column width={4}>
            <SPClusterWorkloadNameContainer disabled={shouldFormBeDisabled} />
          </Grid.Column>
          <Grid.Column width={3}>
            <SPRetentionContainer />
            <RetentionApprovalRequiredContainer isCustomerViewEnabled={false} />
          </Grid.Column>
          <Grid.Column width={3}>
            <SPRetentionInfiniteContainer />
          </Grid.Column>
          <If test={IS_TABLE_FLOW_LIVE}>
            <Grid.Column width={3}>
              <SPClusterTableFlowEnabledContainer />
            </Grid.Column>
          </If>
          <Grid.Column width={3}>
            {shouldShowFFOptionVariable && <SPClusterFollowerFetchContainer />}
          </Grid.Column>
        </Grid.Row>
        <If test={isBaseLineSP}>
          <Grid.Row>
            <Grid.Column width={4}>
              <SPClusterWorkloadExistingStorageContainer disabled={shouldFormBeDisabled} />
            </Grid.Column>
            <Grid.Column width={4}>
              <SPClusterWorkloadCKUContainer disabled={shouldFormBeDisabled} />
            </Grid.Column>
          </Grid.Row>
        </If>
        <Grid.Row>
          <Grid.Column width={4}>
            <SPClusterWorkloadNetworkingTypeContainer disabled={shouldFormBeDisabled} />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </PaddedAndRaisedSegment>
  );
};
